import {
  ColumnOrderState,
  ColumnSizingState,
  createColumnHelper,
  SortingState,
  VisibilityState,
} from '@tanstack/vue-table';
import { isNil, isUndefined } from 'lodash-es';
import { ExtendedGridDataType } from '@/common/utils';
import { CustomColumn } from '@/common/utils/types';
import { DEFAULT_COLUMN_MIN_SIZE } from '../../modernGrid.define';
import { numberWithComma, getRoundedNumberWithComma } from '@/common/utils/core/number/format';
import type {
  GridColumnDef,
  GridRowDataType,
  GridRawRowDataType,
  GridSettingEnvValue,
  CustomSortingFnType,
  ColumnVisibilityInfo,
} from '../../modernGrid.types';

export const useModernGridHelper = <RowDataType extends GridRawRowDataType>() => {
  const columnHelper = createColumnHelper<GridRowDataType<RowDataType>>();

  const getSortingFn = (
    type: ExtendedGridDataType,
    useFormatting: boolean = false,
    rendererType?: string,
  ): CustomSortingFnType<GridRowDataType<RowDataType>> => {
    if (rendererType === 'chip-cell' || rendererType === 'chip-cell-clickable') {
      return 'sortingFnForArray' as CustomSortingFnType<GridRowDataType<RowDataType>>;
    }
    if (rendererType === 'multi-chip-cell') {
      return 'sortingFnForObjectKeys' as CustomSortingFnType<GridRowDataType<RowDataType>>;
    }

    let sortingFn = 'sortingFnForBasic';
    switch (type) {
      case 'string':
        sortingFn = 'sortingFnForString';
        break;
      case 'stringNumber':
      case 'number':
        sortingFn = 'sortingFnForNumber';
        break;
      case 'object':
        sortingFn = 'sortingFnForArray';
        break;
      case 'datetime':
        sortingFn = 'sortingFnForDatetime';
        break;
      case 'age':
        sortingFn = 'sortingFnForAge';
        break;
      default:
        break;
    }
    if (useFormatting) {
      sortingFn = 'sortingFnForNumber';
    }
    return sortingFn as CustomSortingFnType<GridRowDataType<RowDataType>>;
  };

  const createAccessorFn = (
    row: GridRowDataType<RowDataType>,
    cellFormatter: ((_row: GridRowDataType<RowDataType>) => any) | undefined,
    columnId: string,
  ): any => (cellFormatter ? cellFormatter(row) : row[columnId]) ?? '';

  const transformColumns = (columns: CustomColumn[]): GridColumnDef<RowDataType>[] =>
    columns
      .filter(({ hide, searchable, children }) => {
        if (children?.length) return true;
        return !hide || searchable === true;
      })
      .map((column, index) => {
        const {
          field,
          caption,
          width,
          searchable,
          type,
          sortable,
          enableResizing,
          disabledHide,
          decimal,
          formatter,
          sortingFn,
          hide,
          rendererType,
          children,
          ...rest
        } = column;

        // 그룹 컬럼 처리 (children이 있는 경우)
        if (children && children.length > 0) {
          return columnHelper.group({
            id: field || `group_${index}`,
            header: caption,
            enableGlobalFilter: searchable === true,
            columns: transformColumns(children),
            meta: {
              ...rest,
              customHeader: rest.customHeader,
            },
          }) as GridColumnDef<RowDataType>;
        }

        // hide: true && searchable: true인 컬럼은 검색만 가능하도록 설정
        const isHiddenSearchableColumn = hide === true && searchable === true;
        const defaultEnableResizing = !isNil(enableResizing) ? enableResizing : true;
        const columnEnableResizing = isHiddenSearchableColumn ? false : defaultEnableResizing;

        // 자체 포맷팅을 수행하는 rendererType인 경우 기본 formatter 적용하지 않음
        const selfFormattingRenderers = ['sec', 'byte', 'toSec', 'digit', 'deltaSymbolNumber'];
        const hasSelfFormatting = rendererType && selfFormattingRenderers.includes(rendererType);

        let cellFormatter: ((row: GridRowDataType<RowDataType>) => any) | undefined = formatter;
        const useFormatting = !isUndefined(decimal) || type === 'float' || type === 'number';
        if (useFormatting && !formatter && !hasSelfFormatting) {
          cellFormatter = (row): string => {
            const value = row[field];
            if (typeof value !== 'number') return value;

            if (type === 'float' || !isUndefined(decimal)) {
              return getRoundedNumberWithComma(value, decimal ?? 3);
            }
            return numberWithComma(value);
          };
        }

        const columnId = field || `column_${index}`;

        return columnHelper.accessor((row) => createAccessorFn(row, cellFormatter, field), {
          id: columnId,
          header: caption,
          size: width,
          minSize: width && width < DEFAULT_COLUMN_MIN_SIZE ? width : undefined,
          enableGlobalFilter: searchable === true,
          enableSorting: isHiddenSearchableColumn ? false : sortable,
          enableResizing: columnEnableResizing,
          enableHiding: isHiddenSearchableColumn ? false : !disabledHide,
          sortingFn:
            sortingFn || getSortingFn(type as ExtendedGridDataType, useFormatting, rendererType),
          sortUndefined: 'last',
          meta: {
            ...rest,
            type,
            size: width,
            decimal,
            formatter,
            sortingFn,
            rendererType,
            hiddenDisplay: isHiddenSearchableColumn ? true : rest.hiddenDisplay,
            excludeFromColumnList: isHiddenSearchableColumn ? true : rest.excludeFromColumnList,
          },
        }) as GridColumnDef<RowDataType>;
      });

  const migrateEnvValue = (oldEnvValue: any[]): Required<GridSettingEnvValue> | {} => {
    if (!oldEnvValue || !oldEnvValue?.length) return {};

    const columnVisibility: VisibilityState = {};
    const columnOrder: ColumnOrderState = new Array(oldEnvValue.length);
    const columnSizing: ColumnSizingState = {};
    const sorting: SortingState = [];
    const columnSettings: ColumnVisibilityInfo[] = new Array(oldEnvValue.length);
    const orderMapping = new Array(oldEnvValue.length);

    oldEnvValue.forEach(({ field, hiddenDisplay, hide, width, sortOption = {}, index }, ix) => {
      let visibility = true;
      if (!isUndefined(hide)) {
        visibility = !hide;
      } else if (!isUndefined(hiddenDisplay)) {
        visibility = !hiddenDisplay;
      }
      columnVisibility[field] = visibility;

      orderMapping[ix] = {
        field,
        index: index ?? Infinity,
        originalIndex: ix,
      };

      if (width) {
        columnSizing[field] = width as number;
      }

      if (!!sortOption?.sortType && sortOption?.sortType !== 'init') {
        sorting.push({
          id: field,
          desc: sortOption?.sortType === 'desc',
        });
      }

      columnSettings[ix] = {
        text: field,
        fixed: false,
        label: field,
        visibility,
      };
    });

    orderMapping.sort((a, b) => a.index - b.index || a.originalIndex - b.originalIndex);
    orderMapping.forEach((item, i) => {
      columnOrder[i] = item.field;
    });

    return {
      columnVisibility,
      columnOrder,
      columnSizing,
      sorting,
      columnSettingList: columnSettings,
    };
  };

  return {
    createAccessorFn,
    getSortingFn,
    transformColumns,
    migrateEnvValue,
  };
};
